import {ReconciliationReport} from '../types';
import {
    PlatformPropertyCard,
    PlatformPropertyCardCopyButtonDecorator,
    PlatformPropertyCardProperty
} from "@flexinet/ui-components";
import {Code, Group, ScrollArea, Stack, Text, Title} from "@mantine/core";
import {ChannelBadge, DiscrepancyBadge} from "./Badges.tsx";
import {prettyPrintDiscrepancyDetails} from "../utils/parseDiscrepancyDetails.ts";

export const Profile = ({record}: { record: ReconciliationReport }) => {

    const summaryProperties = [
        {
            label: 'ID',
            value: record.id as string,
            decorator: (value: string) => <PlatformPropertyCardCopyButtonDecorator value={value}/>,
            span: 12,
        },
        {
            label: 'Channel',
            value: <ChannelBadge channel={record.channel}/>,
            span: 6,
        },
        {
            label: 'Status',
            value: <DiscrepancyBadge record={record}/>,
            span: 6,
        },
        {
            label: 'Run Date',
            value: (record.runDate as string) ?? '—',
            span: 6,
        },
        {
            label: 'Run Started At',
            value: record.runStartedAt as string,
            formatAs: 'Timestamp',
            span: 6,
        },
        {
            label: 'Run Completed At',
            value: (record.runCompletedAt as string) ?? '—',
            formatAs: 'Timestamp',
            span: 6,
        },
    ] as PlatformPropertyCardProperty[];

    const outcomeProperties = [
        {
            label: 'Ours But Not Theirs',
            value: typeof record.oursButNotTheirsCount === 'number' ? String(record.oursButNotTheirsCount) : '0',
            span: 4,
        },
        {
            label: 'Theirs But Not Ours',
            value: typeof record.theirsButNotOursCount === 'number' ? String(record.theirsButNotOursCount) : '0',
            span: 4,
        },
        {
            label: 'Quantity Mismatch',
            value: typeof record.quantityMismatchCount === 'number' ? String(record.quantityMismatchCount) : '0',
            span: 4,
        },
    ] as PlatformPropertyCardProperty[];

    const details = prettyPrintDiscrepancyDetails(record.discrepancyDetails);

    return (
        <Stack gap={'lg'}>
            <PlatformPropertyCard properties={summaryProperties}/>
            <PlatformPropertyCard properties={outcomeProperties}/>

            <Stack gap={'xs'}>
                <Title order={5}>Discrepancy details</Title>
                {details
                    ? <ScrollArea.Autosize mah={400}>
                        <Code block>{details}</Code>
                    </ScrollArea.Autosize>
                    : <Text c={'dimmed'} size={'sm'}>No discrepancy details for this run.</Text>}
                {record.discrepancyDetailsS3Uri
                    ? <Stack gap={4}>
                        <Text size={'xs'} c={'dimmed'}>Full discrepancies overflowed inline storage:</Text>
                        {/* FNP-593: an s3:// URI has no browser handler, so an Anchor href
                            was a dead link. Render it as copyable monospace text instead —
                            the operator can paste it into the AWS console / CLI. */}
                        <Group gap={'xs'} wrap={'nowrap'} align={'center'}>
                            <Code>{record.discrepancyDetailsS3Uri as string}</Code>
                            <PlatformPropertyCardCopyButtonDecorator value={record.discrepancyDetailsS3Uri as string}/>
                        </Group>
                    </Stack>
                    : null}
            </Stack>
        </Stack>
    );
};
